import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from "recharts";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";
import { fmt, fmtDate, DURUM_COLORS } from "@/lib/format";
import { Wallet, TrendingDown, HandCoins, AlertTriangle, ArrowRight } from "lucide-react";

export default function CariDashboard() {
  const [summary, setSummary] = useState(null);
  const [overdue, setOverdue] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/cari/summary").then((res) => setSummary(res.data));
    api.get("/cari/customers").then((res) => setOverdue(res.data.filter((c) => c.durum === "Vadesi Geçti")));
  }, []);

  const totals = summary?.totals || {};
  const counts = summary?.durum_counts || {};
  const pieData = Object.entries(counts)
    .filter(([, v]) => v > 0)
    .map(([name, value]) => ({ name, value }));

  const KPIS = [
    { key: "toplam_alacak", label: "Toplam Alacak", icon: Wallet, tone: "#26231E" },
    { key: "toplam_tahsilat", label: "Tahsil Edilen", icon: HandCoins, tone: "#2F6E4E" },
    { key: "toplam_kalan", label: "Kalan Bakiye", icon: TrendingDown, tone: "#A63A3A" },
  ];

  return (
    <Layout>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="kicker mb-1">CARİ TAKİP</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Cari Gösterge Paneli</h1>
        </div>
        <button onClick={() => navigate("/cari")} data-testid="go-cari-records" className="flex items-center gap-2 px-4 py-2.5 bg-[#B8933F] hover:bg-[#9C7A33] text-white rounded-sm text-sm font-medium transition-colors duration-150">
          Cari Müşterileri Yönet <ArrowRight className="w-4 h-4" />
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {KPIS.map((kpi) => {
          const Icon = kpi.icon;
          return (
            <div key={kpi.key} className="bg-white border border-[#E7DECC] rounded-sm p-4" data-testid={`cari-kpi-${kpi.key}`}>
              <div className="flex items-center justify-between mb-3">
                <span className="kicker">{kpi.label}</span>
                <Icon className="w-4 h-4" style={{ color: kpi.tone }} />
              </div>
              <div className="font-heading font-black text-xl tabular-nums tracking-tight" style={{ color: kpi.tone }}>
                {fmt(totals[kpi.key])}
              </div>
            </div>
          );
        })}
        <div className="bg-white border border-[#E7DECC] rounded-sm p-4" data-testid="cari-kpi-count">
          <div className="flex items-center justify-between mb-3">
            <span className="kicker">Cari Müşteri</span>
          </div>
          <div className="font-heading font-black text-xl tabular-nums tracking-tight">{totals.musteri_sayisi ?? 0}</div>
          <div className="text-xs text-[#8C8377] mt-1">{totals.kayit_sayisi ?? 0} vade kaydı</div>
        </div>
      </div>

      {/* AF / SF stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white border border-[#E7DECC] rounded-sm p-4" data-testid="cari-kpi-toplam_alis">
          <div className="kicker mb-1.5">Toplam Alış (AF)</div>
          <div className="font-heading font-bold text-lg tabular-nums">{fmt(totals.toplam_alis)}</div>
        </div>
        <div className="bg-white border border-[#E7DECC] rounded-sm p-4" data-testid="cari-kpi-toplam_satis">
          <div className="kicker mb-1.5">Toplam Satış (SF)</div>
          <div className="font-heading font-bold text-lg tabular-nums">{fmt(totals.toplam_satis)}</div>
        </div>
        <div className="bg-white border border-[#E7DECC] rounded-sm p-4" data-testid="cari-kpi-kar">
          <div className="kicker mb-1.5">Tahmini Kâr (SF−AF)</div>
          <div className="font-heading font-bold text-lg tabular-nums text-[#2F6E4E]">
            {fmt((totals.toplam_satis || 0) - (totals.toplam_alis || 0))}
          </div>
        </div>
      </div>

      {/* Durum counts (müşteri bazlı) */}
      <div className="kicker mb-2 text-[#8C8377]">DURUM DAĞILIMI — MÜŞTERİ BAZLI</div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {["Ödendi", "Kısmi Ödendi", "Ödenmedi", "Vadesi Geçti"].map((d) => (
          <div key={d} className="bg-white border border-[#E7DECC] rounded-sm p-4 flex items-center justify-between" data-testid={`durum-count-${d}`}>
            <div>
              <div className="kicker mb-1">{d}</div>
              <div className="font-heading font-bold text-2xl tabular-nums" style={{ color: DURUM_COLORS[d] }}>
                {counts[d] ?? 0}
              </div>
              <div className="text-[11px] text-[#8C8377] mt-0.5">müşteri</div>
            </div>
            <span className="w-3 h-3 rounded-full" style={{ backgroundColor: DURUM_COLORS[d] }} />
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-white border border-[#E7DECC] rounded-sm p-4" data-testid="cari-pie">
          <div className="kicker mb-4">DURUM DAĞILIMI (MÜŞTERİ)</div>
          {pieData.length === 0 ? (
            <div className="h-[280px] flex items-center justify-center text-[#8C8377] text-sm">Veri yok.</div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label>
                  {pieData.map((e, i) => (
                    <Cell key={i} fill={DURUM_COLORS[e.name] || "#8C8377"} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: 2, border: "1px solid #E7DECC" }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="lg:col-span-2 bg-white border border-[#E7DECC] rounded-sm" data-testid="cari-overdue">
          <div className="flex items-center gap-2 px-4 py-3 border-b border-[#E7DECC]">
            <AlertTriangle className="w-4 h-4 text-[#A63A3A]" />
            <span className="font-heading font-bold text-sm tracking-tight">Vadesi Geçen Müşteriler</span>
            <span className="px-2 py-0.5 rounded-sm bg-[#A63A3A]/10 text-[#A63A3A] text-xs font-semibold">{overdue.length} müşteri</span>
          </div>
          <div className="overflow-x-auto max-h-[280px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-[#F3ECDD]">
                <tr className="border-b border-[#E7DECC]">
                  <th className="px-3 py-2 kicker text-left">Müşteri</th>
                  <th className="px-3 py-2 kicker text-right">Vade Sayısı</th>
                  <th className="px-3 py-2 kicker text-left">En Yakın Vade</th>
                  <th className="px-3 py-2 kicker text-right">Kalan</th>
                </tr>
              </thead>
              <tbody>
                {overdue.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-3 py-8 text-center text-[#8C8377]">Vadesi geçen müşteri yok.</td>
                  </tr>
                ) : (
                  overdue.map((r) => (
                    <tr key={r.key} className="border-b border-[#E7DECC] hover:bg-[#EFE7D6] cursor-pointer transition-colors duration-150" onClick={() => navigate(`/cari-musteri/${encodeURIComponent(r.musteri)}`)} data-testid={`cari-overdue-row-${r.key}`}>
                      <td className="px-3 py-2 font-medium text-[#B8933F]">{r.musteri}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{r.vade_sayisi}</td>
                      <td className="px-3 py-2 tabular-nums">{r.en_yakin_vade ? fmtDate(r.en_yakin_vade) : "-"}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-[#A63A3A]">{fmt(r.toplam_kalan)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
}
